/**
 * GoogleSignInButton — renders Google's official "Continue with Google" button.
 *
 * The <GoogleLogin> component (from @react-oauth/google) shows Google's button
 * and, once the student signs in, hands us `credential` — a signed ID token. We
 * pass that to our backend, which verifies it and returns a session.
 *
 * If VITE_GOOGLE_CLIENT_ID isn't set yet, we show a friendly setup note instead
 * of a broken button (the app still runs; login just isn't wired up yet).
 */

import { useLayoutEffect, useRef, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { AlertCircle } from 'lucide-react';
import { Spinner } from '@/components/ui';
import { useCustomerAuth } from '@/context/CustomerAuthContext';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/**
 * Google renders its button inside an IFRAME at a fixed pixel width — it has
 * no notion of "100%", so nothing about it responds to the screen on its own.
 * A hardcoded 300 overflowed the sign-in card on every phone narrower than
 * ~400px, and on a 320px screen the button was 76px wider than the space it
 * had and spilled outside the card. So we measure the space and tell Google.
 *
 * The bounds are Google's: it caps the button at 400px, and below ~200px the
 * label has nowhere to go. Every realistic phone lands inside that range.
 */
const MIN_BUTTON_WIDTH = 200;
const MAX_BUTTON_WIDTH = 400;

export function GoogleSignInButton({ onSignedIn }) {
  const { loginWithGoogle } = useCustomerAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  /** The slot the button has to fit into, measured rather than assumed. */
  const slotRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(0);

  /**
   * useLayoutEffect, not useEffect: this measures and sets state before the
   * browser paints, so the button appears at its final width instead of
   * flashing at the wrong one and resizing.
   */
  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot) return undefined;

    const measure = () => {
      const width = Math.round(slot.getBoundingClientRect().width);
      if (width > 0) {
        setButtonWidth(Math.max(MIN_BUTTON_WIDTH, Math.min(MAX_BUTTON_WIDTH, width)));
      }
    };

    measure();

    // Guarded: on a browser without ResizeObserver this would throw straight
    // out of the effect and take the whole sign-in page with it. The one-off
    // measurement above has already run, so such a browser still gets a
    // correctly sized button — it just will not follow a rotation.
    if (typeof ResizeObserver === 'undefined') return undefined;

    // Catches rotation, desktop window resizing, and the on-screen keyboard —
    // anything that changes the slot without remounting this component.
    const observer = new ResizeObserver(measure);
    observer.observe(slot);
    return () => observer.disconnect();
  }, []);

  if (!CLIENT_ID) {
    return (
      <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
        <AlertCircle size={16} className="mt-0.5 shrink-0" />
        <span>
          Google Sign-In isn’t configured yet. Add your <code>VITE_GOOGLE_CLIENT_ID</code> to enable
          it.
        </span>
      </div>
    );
  }

  const handleSuccess = async (response) => {
    setBusy(true);
    setError(null);
    try {
      const result = await loginWithGoogle(response.credential);
      onSignedIn?.(result);
    } catch (err) {
      setError(err.message ?? 'Sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-center gap-3">
      {/*
        `w-full` and `min-h` on the slot: it must span the space the button is
        allowed to use so there is something to measure, and hold that height
        while Google's script loads so the card does not jump when it arrives.
      */}
      <div ref={slotRef} className="flex min-h-11 w-full justify-center">
        {busy ? (
          <div className="flex items-center gap-2 text-sm text-muted">
            <Spinner size="sm" className="text-primary-600" /> Signing you in…
          </div>
        ) : (
          /*
           * Always rendered, never gated on the measurement. useLayoutEffect
           * sets the real width before the browser paints, so this falls back
           * to the minimum for one un-painted frame at most — and if measuring
           * ever failed outright, a slightly narrow button is a far better
           * outcome than a sign-in page with no button on it.
           */
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={() => setError('Google sign-in was cancelled or failed.')}
            shape="pill"
            size="large"
            text="continue_with"
            width={String(buttonWidth || MIN_BUTTON_WIDTH)}
          />
        )}
      </div>
      {error && <p className="text-center text-sm font-medium text-danger">{error}</p>}
    </div>
  );
}

export default GoogleSignInButton;
